<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core'
import { computed, ref } from 'vue'
import { RouteLocationRaw, useRoute } from 'vue-router'

import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { moduleInformation, moduleLicenses } from '@/utils/licenses'
import { useAlarmStore } from '@/stores/alarm-store'

import SidebarNavLink from '@/components/layout/navigation/SidebarNavLink.vue'

defineProps<{
    isOpen?: boolean
    isCollapsed?: boolean
}>()

const emit = defineEmits<{
    close: []
    toggleCollapse: []
}>()

const authStore = useAuthStore()
const alarmStore = useAlarmStore()
const route = useRoute()
const sidebar = ref<HTMLElement>()

const alarmRoute = computed<RouteLocationRaw | null>(() => {
    if (route.name?.toString().includes('packaging')) {
        return { name: 'packaging.alarms' }
    } else if (route.name?.toString().includes('dmr')) {
        return { name: 'dmr.alarms' }
    } else if (route.name?.toString().includes('dm.')) {
        return { name: 'dm.alarms' }
    }

    if (authStore.hasLicense([LicenseType.PackagingModule, LicenseType.PackagingModuleReadonly])) {
        return { name: 'packaging.alarms' }
    } else if (
        authStore.hasLicense([
            LicenseType.DamageReportModule,
            LicenseType.DamageReportModuleReadonly,
        ])
    ) {
        return { name: 'dmr.alarms' }
    } else if (
        authStore.hasLicense([
            LicenseType.DeliveryManagementModule,
            LicenseType.DeliveryManagementModuleReadonly,
        ])
    ) {
        return { name: 'dm.alarms' }
    }

    return null
})

onClickOutside(sidebar, () => emit('close'))
</script>

<template>
    <aside
        ref="sidebar"
        class="fixed inset-y-0 left-0 z-50 transform bg-dark-700 text-primary-200 transition-all duration-300 ease-in-out lg:translate-x-0"
        :class="{
            'translate-x-0': isOpen,
            '-translate-x-full': !isOpen,
            'w-64': !isCollapsed,
            'w-16': isCollapsed,
        }"
    >
        <!-- Header with Animated Livrero Logo -->
        <div class="relative px-3 py-4 mb-2">
            <!-- Logo -->
            <div>
                <div class="group cursor-pointer select-none px-3" @click="emit('toggleCollapse')">
                    <h1
                        v-if="!isCollapsed"
                        class="text-3xl font-bold text-primary-200 mb-1 transition-all duration-500 group-hover:text-yellow-500 group-hover:drop-shadow-xl"
                    >
                        <span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 0ms"
                            >L</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 100ms"
                            >i</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 200ms"
                            >v</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 300ms"
                            >r</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 400ms"
                            >e</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 500ms"
                            >r</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 600ms"
                            >o</span
                        >
                    </h1>
                    <h1
                        v-else
                        class="text-3xl font-bold text-primary-200 mb-1 text-center transition-all duration-500 group-hover:text-yellow-500 group-hover:drop-shadow-xl"
                    >
                        <span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            >L</span
                        >
                    </h1>

                    <div
                        v-if="!isCollapsed"
                        class="h-1 w-20 bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full transition-all duration-700 group-hover:w-32 group-hover:h-2 group-hover:shadow-lg group-hover:shadow-yellow-500/50 group-hover:animate-pulse"
                    ></div>
                    <div
                        v-else
                        class="h-1 w-6 mx-auto bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full"
                    ></div>
                </div>
            </div>

            <!-- Close button for mobile - repositioned to top right -->
            <button
                class="lg:hidden text-primary-200 hover:text-yellow-500 absolute top-2 right-5"
                @click="emit('close')"
            >
                <MdiClose class="h-5 w-5" />
            </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 space-y-1 px-3 py-4" :class="{ 'px-2': isCollapsed }">
            <SidebarNavLink
                :to="{ name: 'packaging' }"
                :translation="moduleInformation[LicenseType.PackagingModule].translation"
                :is-collapsed="isCollapsed"
                :activated="
                    authStore.hasLicense([
                        LicenseType.PackagingModule,
                        LicenseType.PackagingModuleReadonly,
                    ])
                "
            >
                <MdiWarehouse class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'dmr' }"
                :translation="moduleInformation[LicenseType.DamageReportModule].translation"
                :is-collapsed="isCollapsed"
                :activated="
                    authStore.hasLicense([
                        LicenseType.DamageReportModule,
                        LicenseType.DamageReportModuleReadonly,
                    ])
                "
            >
                <MdiTruckCargoContainer class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="authStore.hasLicense(moduleLicenses) ? { name: 'dashboards' } : ''"
                :translation="moduleInformation[LicenseType.DashboardModule].translation"
                :is-collapsed="isCollapsed"
                :activated="authStore.hasLicense([LicenseType.DashboardModule])"
            >
                <MdiViewDashboard class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'dr' }"
                :translation="moduleInformation[LicenseType.DriverReportModule].translation"
                :is-collapsed="isCollapsed"
                :activated="
                    authStore.hasLicense([
                        LicenseType.DriverReportModule,
                        LicenseType.DriverReportModuleReadonly,
                    ])
                "
            >
                <MdiTimerMarkerOutline class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'dm' }"
                :translation="moduleInformation[LicenseType.DeliveryManagementModule].translation"
                :is-collapsed="isCollapsed"
                :activated="
                    authStore.hasLicense([
                        LicenseType.DeliveryManagementModule,
                        LicenseType.DeliveryManagementModuleReadonly,
                    ])
                "
            >
                <MdiMapMarker class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'pod' }"
                :translation="moduleInformation[LicenseType.PODModule].translation"
                :is-collapsed="isCollapsed"
                :activated="
                    authStore.hasLicense([LicenseType.PODModule, LicenseType.PODModuleReadonly])
                "
            >
                <MdiFileDocumentCheckOutline class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'ecmr' }"
                :translation="moduleInformation[LicenseType.ECMRModule].translation"
                :is-collapsed="isCollapsed"
                :activated="authStore.hasLicense(LicenseType.ECMRModule)"
            >
                <MdiFileCertificateOutline class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                v-if="authStore.hasLicense(LicenseType.CargoLoadingModule)"
                :to="authStore.hasLicense(moduleLicenses) ? { name: 'cl' } : ''"
                :translation="moduleInformation[LicenseType.CargoLoadingModule].translation"
                :is-collapsed="isCollapsed"
                :activated="authStore.hasLicense([LicenseType.CargoLoadingModule])"
            >
                <MdiPackage class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="
                    authStore.hasLicense(moduleLicenses) || authStore.isReadonly.globalReadonly
                        ? { name: 'settings' }
                        : ''
                "
                translation="settings"
                :is-collapsed="isCollapsed"
                :activated="
                    authStore.hasLicense(moduleLicenses) || authStore.isReadonly.globalReadonly
                "
            >
                <MdiCog class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                v-if="authStore.companyId && alarmRoute"
                :to="alarmRoute"
                translation="alarms"
                :is-collapsed="isCollapsed"
                :activated="true"
            >
                <div class="relative">
                    <MdiBell v-if="alarmStore.counts.total > 0" class="h-5 w-5" />
                    <MdiBellOutline v-else class="h-5 w-5" />

                    <span
                        v-if="alarmStore.counts.total > 0"
                        class="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold"
                        v-text="alarmStore.counts.total"
                    />
                </div>
            </SidebarNavLink>
        </nav>

        <!-- Collapse toggle button (desktop only) -->
        <div
            class="hidden lg:block p-3 mt-auto border-t border-gray-700/30"
            :class="{ 'p-2': isCollapsed }"
        >
            <button
                v-tooltip="{
                    content: isCollapsed ? 'Expand menu' : 'Collapse menu',
                    placement: 'right',
                    disabled: !isCollapsed,
                }"
                class="flex items-center w-full p-2 rounded-lg text-primary-200 hover:bg-gray-800/40 hover:text-yellow-500 transition-all duration-200"
                :class="{ 'justify-center': isCollapsed }"
                @click="emit('toggleCollapse')"
            >
                <MdiChevronDoubleLeft v-if="!isCollapsed" class="h-5 w-5" />
                <MdiChevronDoubleRight v-else class="h-5 w-5" />
                <span v-if="!isCollapsed" class="ml-3 text-sm">Collapse</span>
            </button>
        </div>
    </aside>

    <!-- Mobile backdrop -->
    <div
        v-if="isOpen"
        class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
        @click="emit('close')"
    />
</template>

<style scoped>
/* Logo Animation - adapted for sidebar */
@keyframes logo-bounce {
    0%,
    100% {
        transform: translateY(0) scale(1);
        text-shadow: 0 0 0 rgba(255, 209, 40, 0);
    }
    25% {
        transform: translateY(-4px) scale(1.05);
        text-shadow: 0 2px 4px rgba(255, 209, 40, 0.3);
    }
    50% {
        transform: translateY(-6px) scale(1.1);
        text-shadow: 0 4px 8px rgba(255, 209, 40, 0.4);
    }
    75% {
        transform: translateY(-3px) scale(1.025);
        text-shadow: 0 3px 6px rgba(255, 209, 40, 0.3);
    }
}

.animate-logo-bounce {
    animation: logo-bounce 0.8s ease-out;
}
</style>
